import { useMemo } from "react";
import type { LoadPhase } from "src-next/modules/clinical-insights/loadPhase";
import type {
  AnalysisCardId,
  ClinicalInsightDateRange,
} from "src-next/modules/clinical-insights/types/analysisTypes";
import {
  deriveInsightLoadPhase,
  useCachedInsightPayload,
  useReportInsightLoadPhase,
} from "./useCachedInsightPayload";
import { useInsightPatientHydration } from "./useInsightPatientHydration";

interface UseFhirInsightViewArgs<T> {
  analysisId: AnalysisCardId;
  organizationId: string;
  dateRange?: ClinicalInsightDateRange;
  enabled: boolean;
  isActive: boolean;
  onLoadPhaseChange?: (phase: LoadPhase) => void;
  fetcher: () => Promise<T[]>;
  selectPatientIds: (resources: T[]) => string[];
}

/** Bundle FHIR da organização em cache de sessão, com pacientes hidratados à parte. */
export function useFhirInsightView<T>({
  analysisId,
  organizationId,
  dateRange,
  enabled,
  isActive,
  onLoadPhaseChange,
  fetcher,
  selectPatientIds,
}: UseFhirInsightViewArgs<T>) {
  const { data, isLoading, error, refresh } = useCachedInsightPayload<T[]>({
    analysisId,
    organizationId,
    dateRange,
    enabled: enabled && Boolean(organizationId),
    fetcher,
  });

  const resources = data ?? [];
  const patientIds = useMemo(
    () => (data ? selectPatientIds(data) : []),
    [data, selectPatientIds],
  );
  const hydration = useInsightPatientHydration(patientIds, data !== undefined);

  const loadPhase = deriveInsightLoadPhase({
    enabled: enabled && Boolean(organizationId),
    isLoading,
    error,
    hasPayload: data !== undefined,
    patientsLoading: hydration.isLoading,
  });

  useReportInsightLoadPhase(isActive, loadPhase, onLoadPhaseChange);

  return {
    resources,
    hasPayload: data !== undefined,
    isLoading,
    error,
    refresh,
    loadPhase,
    patients: hydration.patients,
    patientsProgress: hydration.progress,
    patientsLoading: hydration.isLoading,
  };
}
